/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/agentsApi.ts (Agents)
 🗓️ Created: 2025-01-15
 🧩 Description:
    API service layer for Agents.
    Centralizes all API calls for better maintainability.

────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  ListAgentsParams,
  ListAgentsResponse,
  AgentResponse,
  QuickListItem
} from '../types';

/**
 * API endpoint
 */
export const AGENTS_ENDPOINT = '/api/telephony/agents';

/**
 * Normalize headers for fetch
 */
function normalizeHeaders(headers?: HeadersInit): Record<string, string> {
  if (!headers) {
    return {};
  }

  if (headers instanceof Headers) {
    return Object.fromEntries(headers.entries());
  }

  if (Array.isArray(headers)) {
    return headers.reduce<Record<string, string>>((accumulator, [key, value]) => {
      accumulator[key] = value;
      return accumulator;
    }, {});
  }

  return headers;
}

/**
 * Request API with error handling
 */
async function requestApi<T>(
  endpoint: string,
  fallbackMessage: string,
  options: RequestInit = {}
): Promise<{ success: true; data: T; message?: string; pagination?: { page: number; limit: number; total: number; totalPages: number } }> {
  const response = await fetch(
    buildApiUrl(endpoint),
    {
      ...options,
      headers: {
        ...getApiHeaders(),
        ...normalizeHeaders(options.headers),
        ...(options.body && { 'Content-Type': 'application/json' })
      }
    }
  );

  let payload: unknown;

  try {
    payload = await response.json();
  } catch {
    payload = undefined;
  }

  if (!response.ok) {
    const message = payload && typeof payload === 'object' && 'message' in payload
      ? (payload.message as string | undefined)
      : fallbackMessage;
    throw new Error(message || fallbackMessage);
  }

  if (!payload || (typeof payload === 'object' && 'success' in payload && payload.success !== true)) {
    const message = payload && typeof payload === 'object' && 'message' in payload
      ? (payload.message as string | undefined)
      : fallbackMessage;
    throw new Error(message || fallbackMessage);
  }

  if (payload && typeof payload === 'object' && 'success' in payload && payload.success) {
    return payload as { success: true; data: T; message?: string; pagination?: { page: number; limit: number; total: number; totalPages: number } };
  }

  return {
    success: true,
    data: undefined as T
  };
}

/**
 * Fetch agents list
 */
export async function fetchAgents(
  params: ListAgentsParams
): Promise<ListAgentsResponse> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder
  });

  if (params.search) {
    queryParams.set('search', params.search);
  }

  if (params.domainId != null) {
    queryParams.set('domainId', params.domainId.toString());
  }

  const result = await requestApi<AgentResponse[]>(
    `${AGENTS_ENDPOINT}?${queryParams.toString()}`,
    'Failed to fetch agents'
  );

  return {
    success: result.success,
    data: Array.isArray(result.data) ? result.data : [],
    pagination: result.pagination || {
      page: params.page,
      limit: params.limit,
      total: 0,
      totalPages: 0
    }
  };
}

/**
 * Fetch single agent by ID
 */
export async function fetchAgentById(
  id: string | number
): Promise<AgentResponse> {
  const result = await requestApi<AgentResponse>(
    `${AGENTS_ENDPOINT}/${id}`,
    'Failed to fetch agent'
  );
  return result.data;
}

/**
 * Create agent
 */
export async function createAgent(
  payload: {
    name: string;
    extension: string;
    contact?: string | null;
    status?: string;
    ready?: boolean;
    enabled?: boolean;
    settings?: unknown;
  }
): Promise<AgentResponse> {
  const result = await requestApi<AgentResponse>(
    AGENTS_ENDPOINT,
    'Failed to create agent',
    {
      method: 'POST',
      body: JSON.stringify(payload)
    }
  );
  return result.data;
}

/**
 * Update agent
 */
export async function updateAgent(
  id: string | number,
  payload: {
    name?: string;
    extension?: string;
    contact?: string | null;
    status?: string;
    ready?: boolean;
    enabled?: boolean;
    settings?: unknown;
  }
): Promise<AgentResponse> {
  const result = await requestApi<AgentResponse>(
    `${AGENTS_ENDPOINT}/${id}`,
    'Failed to update agent',
    {
      method: 'PUT',
      body: JSON.stringify(payload)
    }
  );
  return result.data;
}

/**
 * Delete agent by ID
 */
export async function deleteAgentById(
  id: string | number
): Promise<void> {
  await requestApi<null>(
    `${AGENTS_ENDPOINT}/${id}`,
    'Failed to delete agent',
    {
      method: 'DELETE'
    }
  );
}

/**
 * Fetch agents for quick list
 */
export async function fetchQuickListItems(
  limit: number = 100
): Promise<QuickListItem[]> {
  const result = await fetchAgents({
    page: 1,
    limit,
    sortBy: 'name',
    sortOrder: 'asc'
  });
  return result.data.map(item => ({
    id: item.id,
    name: item.name,
    extension: item.extension || null
  }));
}

